import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ShieldCheck, User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Utenti() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => setUsers(await api.get("/users")), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "" }); setModal(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "" }); setModal(true); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome.");
    if (!editing && !form.email.trim()) return toast.error("Inserisci l'email.");
    if (!editing && form.password.length < 6) return toast.error("Password di almeno 6 caratteri.");
    if (editing && form.password && form.password.length < 6) return toast.error("Password di almeno 6 caratteri.");
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name.trim() };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.user_id}`, payload);
        toast.success("Utente aggiornato.");
      } else {
        await api.post("/users", { name: form.name.trim(), email: form.email.trim(), password: form.password });
        toast.success("Dipendente creato con successo.");
      }
      setModal(false); load();
    } catch (err) {
      toast.error(err.message || "Operazione non riuscita.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.del(`/users/${toDelete.user_id}`);
      toast.success("Utente spostato nel cestino.");
      setToDelete(null); load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Utenti</h1>
          <p className="text-sm text-muted-foreground mt-1">Crea e gestisci le credenziali dei tuoi dipendenti.</p>
        </div>
        <Button onClick={openNew} data-testid="add-user-button"
          className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
          <Plus className="h-4 w-4" /> Nuovo Dipendente
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead>
                  <TableHead>Creato il</TableHead><TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const owner = u.role === "owner";
                  return (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="font-medium flex items-center gap-2">
                        {owner ? <ShieldCheck className="h-4 w-4 text-amber-600" /> : <User className="h-4 w-4 text-muted-foreground" />}
                        {u.name}{u.user_id === me?.user_id && <span className="text-xs text-muted-foreground">(tu)</span>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${owner ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
                          {owner ? "Amministratore" : "Dipendente"}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{fmtDate(u.created_at)}</TableCell>
                      <TableCell>
                        {!owner && (
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-user-${u.user_id}`} onClick={() => openEdit(u)}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-user-${u.user_id}`} onClick={() => setToDelete(u)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="sm:max-w-md" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Dipendente" : "Nuovo Dipendente"}</DialogTitle>
            <DialogDescription>Le credenziali permetteranno al dipendente di accedere e inserire dati (senza eliminare né gestire utenti).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome e Cognome</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Mario Bianchi" />
            </div>
            <div className="space-y-1.5">
              <Label>Email {editing && <span className="text-xs text-muted-foreground">(non modificabile)</span>}</Label>
              <Input type="email" data-testid="user-email-input" value={form.email} disabled={Boolean(editing)}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="mario@ellevi.it" />
            </div>
            <div className="space-y-1.5">
              <Label>Password {editing && <span className="text-xs text-muted-foreground">(lascia vuoto per non cambiarla)</span>}</Label>
              <Input type="password" data-testid="user-password-input" value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Almeno 6 caratteri" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Annulla</Button>
            <Button onClick={submit} disabled={saving} data-testid="user-save-button"
              className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" verrà spostato nel cestino e non potrà più accedere.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
